import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Stack,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ShieldIcon from '@mui/icons-material/Shield';
import HistoryIcon from '@mui/icons-material/History'; // Added for Login Activity
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleUserMenuClose();
    handleMobileMenuClose();
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    handleMobileMenuClose();
    navigate('/');
  };

  // Header Navigation Items
  const drawerItems = [
    { label: 'Register Business', persianLabel: 'ثبت کسب و کار', path: '/user/business/register', isAction: true },
    { label: 'Contact Us', persianLabel: 'تماس با ما', path: '/contact' },
    { label: 'Cheesta Services', persianLabel: 'خدمات چیستا', path: '/cheesta-services' },
    { label: 'IT Consultation', persianLabel: '\u2067دریافت مشاوره IT\u2069', path: '/hire-us' },
    { label: 'Pricing', persianLabel: 'تعرفه‌ها و آگهی ویژه', path: '/pricing' },
    { label: 'About Cheesta', persianLabel: 'درباره چیستا', path: '/about-cheesta-iranain-australia' },
    { label: 'Cheesta Help', persianLabel: 'راهنمای چیستا', path: '/cheesta-help' },
  ];

  const linkStyle = {
    color: '#ffffff',
    fontSize: '0.85rem',
    fontWeight: 400,
    textTransform: 'none',
    px: 1,
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    '&:hover': {
      bgcolor: 'transparent',
      opacity: 0.8
    }
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: '#2854a1',
        color: '#ffffff',
        boxShadow: 'none',
        zIndex: theme.zIndex.drawer + 1,
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ height: 60, display: 'flex', justifyContent: 'space-between' }}>

          {/* Left Section: Logo and Search */}
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                '&:hover': {
                  opacity: 0.9
                }
              }}
            >
              <img
                src="/logo.png"
                alt="Cheesta"
                style={{
                  display: 'block'
                }}
              />
            </Box>
            {!isMobile && (
              <IconButton
                onClick={() => navigate('/search')}
                size="small"
                sx={{ color: '#ffffff' }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>

          {/* Center Section: Spacer */}
          <Box sx={{ flexGrow: 1 }} />


          {/* Right Section: Register Business and User Menu */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {!isMobile && (
              <>
                <Button
                  component={RouterLink}
                  to="/user/business/register"
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  ثبت کسب و کار
                </Button>
                <Button
                  component={RouterLink}
                  to="/contact"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  تماس با ما
                </Button>
                <Button
                  component={RouterLink}
                  to="/cheesta-services"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  خدمات چیستا
                </Button>
                <Button
                  component={RouterLink}
                  to="/hire-us"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  {'\u2067دریافت مشاوره IT\u2069'}
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  تعرفه‌ها
                </Button>
                <Button
                  component={RouterLink}
                  to="/about-cheesta-iranain-australia"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  درباره چیستا
                </Button>
                <Button
                  component={RouterLink}
                  to="/cheesta-help"
                  sx={{ ...linkStyle, fontWeight: 500, mr: 1 }}
                >
                  راهنمای چیستا
                </Button>

                {/* User Menu or Login Button */}
                {isAuthenticated ? (
                  <Button
                    onClick={handleUserMenuOpen}
                    endIcon={<KeyboardArrowDownIcon />}
                    sx={{ ...linkStyle, pl: 2 }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {user?.name || 'حساب من'}
                      </Typography>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: 'rgba(255,255,255,0.2)',
                          fontSize: '0.9rem'
                        }}
                      >
                        {user?.name?.charAt(0) || 'U'}
                      </Avatar>
                    </Stack>
                  </Button>
                ) : (
                  <Button
                    component={RouterLink}
                    to="/login"
                    sx={{ ...linkStyle, fontWeight: 500, px: 2 }}
                  >
                    ورود
                  </Button>
                )}
              </>
            )}

            {isMobile && (
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={{ color: '#ffffff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Stack>

          {/* User Menu Dropdown Content */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                width: 220,
                borderRadius: 2,
                mt: 1.5,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                direction: 'rtl'
              }
            }}
          >
            <MenuItem onClick={() => handleNavigation('/user')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SpeedIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="پنل کاربری" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>
            
            <MenuItem onClick={() => handleNavigation('/user/performance')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TrendingUpIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="تحلیل عملکرد" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/website-scan')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <TravelExploreIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="اسکن خودکار وب‌سایت" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/scanned-websites')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ManageSearchIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="وب‌سایت‌های اسکن‌شده" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/security')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ShieldIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="امنیت و سلامت وب‌سایت" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/promotions')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ReceiptLongIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="تبلیغات و پرداخت‌ها" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/settings')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <SettingsIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="حساب کاربری" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>

            <MenuItem onClick={() => handleNavigation('/user/login-activity')}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <HistoryIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText primary="فعالیت‌های ورود" primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>
            
            {user?.role === 'admin' && (
              <MenuItem onClick={() => handleNavigation('/admin')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <SpeedIcon fontSize="small" color="secondary" />
                </ListItemIcon>
                <ListItemText primary="پنل مدیریت" primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            )}
            <Divider sx={{ my: 1 }} />
            {isAuthenticated ? (
              <MenuItem onClick={handleLogout}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                </ListItemIcon>
                <ListItemText primary="خروج از حساب" primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            ) : (
              <MenuItem onClick={() => handleNavigation('/login')}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <LogoutIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                </ListItemIcon>
                <ListItemText primary="ورود به حساب" primaryTypographyProps={{ fontSize: '0.85rem' }} />
              </MenuItem>
            )}
          </Menu>

          {/* Mobile Drawer Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            sx={{ direction: 'rtl' }}
            PaperProps={{
              sx: {
                width: 250, borderRadius: 2, mt: 1,
                // Same text size as the desktop account menu (was the 16px default).
                '& .MuiListItemText-primary': { fontSize: '0.875rem' },
                '& .MuiMenuItem-root': { minHeight: 42, py: 0.5 },
                '& .MuiListItemIcon-root': { minWidth: 34 },
              }
            }}
          >
            <MenuItem onClick={() => handleNavigation('/search')}>
              <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="جستجو" />
            </MenuItem>
            {drawerItems.map((item) => (
              <MenuItem key={item.path} onClick={() => handleNavigation(item.path)}>
                <ListItemIcon>{item.isAction ? <AddIcon fontSize="small" /> : null}</ListItemIcon>
                <ListItemText primary={item.persianLabel} />
              </MenuItem>
            ))}
            <Divider />
            {/* Same icon + text rows as the items above, so everything lines up
                (these were plain text at a larger size, flush against the edge).
                An array rather than a fragment: MUI Menu needs direct children. */}
            {isAuthenticated ? [
              <MenuItem key="user" onClick={() => handleNavigation('/user')}>
                <ListItemIcon><SpeedIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="پنل کاربری" />
              </MenuItem>,
              <MenuItem key="performance" onClick={() => handleNavigation('/user/performance')}>
                <ListItemIcon><TrendingUpIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="تحلیل عملکرد" />
              </MenuItem>,
              <MenuItem key="scan" onClick={() => handleNavigation('/user/website-scan')}>
                <ListItemIcon><TravelExploreIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="اسکن خودکار وب‌سایت" />
              </MenuItem>,
              <MenuItem key="scanned" onClick={() => handleNavigation('/user/scanned-websites')}>
                <ListItemIcon><ManageSearchIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="وب‌سایت‌های اسکن‌شده" />
              </MenuItem>,
              <MenuItem key="security" onClick={() => handleNavigation('/user/security')}>
                <ListItemIcon><ShieldIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="امنیت و سلامت وب‌سایت" />
              </MenuItem>,
              <MenuItem key="promotions" onClick={() => handleNavigation('/user/promotions')}>
                <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="تبلیغات و پرداخت‌ها" />
              </MenuItem>,
              <MenuItem key="settings" onClick={() => handleNavigation('/user/settings')}>
                <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="حساب کاربری" />
              </MenuItem>,
              <MenuItem key="activity" onClick={() => handleNavigation('/user/login-activity')}>
                <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="فعالیت‌های ورود" />
              </MenuItem>,
              user?.role === 'admin' ? (
                <MenuItem key="admin" onClick={() => handleNavigation('/admin')}>
                  <ListItemIcon><SpeedIcon fontSize="small" color="secondary" /></ListItemIcon>
                  <ListItemText primary="پنل مدیریت" />
                </MenuItem>
              ) : null,
              <MenuItem key="logout" onClick={handleLogout} sx={{ color: 'error.main' }}>
                <ListItemIcon><LogoutIcon fontSize="small" color="error" sx={{ transform: 'rotate(180deg)' }} /></ListItemIcon>
                <ListItemText primary="خروج" />
              </MenuItem>,
            ] : (
              <MenuItem onClick={() => handleNavigation('/login')}>
                <ListItemIcon><LogoutIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} /></ListItemIcon>
                <ListItemText primary="ورود" />
              </MenuItem>
            )}
          </Menu>

        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
